import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../lib/api';
import { cn, timeAgo } from '../lib/utils';
import { Bell, CheckCheck, AlertTriangle, CheckCircle2, Info, Zap, X } from 'lucide-react';
import { Badge } from './ui/badge';

const TYPE_ICONS = {
  escalation: AlertTriangle,
  agent_completed: CheckCircle2,
  tool_proposal: Zap,
  sla_warning: AlertTriangle,
  integration: CheckCircle2,
  close_progress: Info,
  orchestration_task: Zap,
};

const SEVERITY_COLORS = {
  critical: 'text-red-600 bg-red-50',
  warning: 'text-amber-600 bg-amber-50',
  success: 'text-emerald-600 bg-emerald-50',
  info: 'text-blue-600 bg-blue-50',
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const loadNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.list({ limit: 10 });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMarkAllRead = async () => {
    await notificationAPI.markAllRead();
    loadNotifications();
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      await notificationAPI.markRead(notif.id);
    }
    setOpen(false);
    if (notif.link) navigate(notif.link);
    loadNotifications();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-4.5 h-4.5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-xl shadow-lg border overflow-hidden z-50 animate-fade-in-up" data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="font-display font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1">
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
            </div>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-10">
                <Bell className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                <p className="text-xs text-slate-400">No notifications</p>
              </div>
            ) : (
              notifications.map(notif => {
                const Icon = TYPE_ICONS[notif.type] || Info;
                const color = SEVERITY_COLORS[notif.severity] || SEVERITY_COLORS.info;
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={cn(
                      'w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b last:border-0',
                      !notif.read && 'bg-teal-50/30'
                    )}
                    data-testid="notification-item"
                  >
                    <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5', color)}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm truncate', !notif.read ? 'font-medium text-slate-900' : 'text-slate-700')}>{notif.title}</p>
                      <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{notif.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                    {!notif.read && <span className="w-2 h-2 rounded-full bg-teal-500 mt-2 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
